export type LinkTarget =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; href: string }
  | { kind: 'unsafe' };

const SAFE_EXTERNAL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

/**
 * What a link in an assistant reply is allowed to do.
 *
 * The model writes links from `dashboard_url` values, but the prose around them
 * is shaped by tool results that carry text typed by tenants and by anonymous
 * visitors. An allow-list of protocols is the only safe reading: anything that
 * is not plainly http(s) or mailto is rendered as text, never as an anchor.
 */
export function classifyLink(href: string, currentOrigin: string): LinkTarget {
  const trimmed = href.trim();
  if (!trimmed) return { kind: 'unsafe' };

  let url: URL;
  try {
    url = new URL(trimmed, currentOrigin);
  } catch {
    return { kind: 'unsafe' };
  }

  if (!SAFE_EXTERNAL_PROTOCOLS.has(url.protocol)) return { kind: 'unsafe' };

  if (url.protocol !== 'mailto:' && url.origin === currentOrigin) {
    const path = `${url.pathname}${url.search}${url.hash}`;
    // A same-origin href of e.g. "http://localhost:3000//evil.com/x" parses
    // with the right `origin`, but its pathname starts with "//evil.com/x" —
    // a network-path reference. Handed to Next's <Link> on its own (no origin
    // to re-anchor it) that string is itself protocol-relative: Next's router
    // treats a leading "/" as "local" (no scheme check on what follows) and
    // then resolves the href against `location.href`, landing on
    // `http://evil.com/x` as a full same-tab navigation — exactly the class
    // of bypass the origin check above exists to prevent. Browsers and some
    // parsers also fold a leading "\" into "/" in this position, so collapse
    // any leading run of "/" or "\" — one or many, mixed or not — to a single
    // "/" before it ever reaches a link. A normal path only ever has one
    // leading "/" already, so this is a no-op for every legitimate link.
    return { kind: 'internal', path: path.replace(/^[/\\]+/, '/') };
  }
  return { kind: 'external', href: trimmed };
}
